import { useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Skeleton } from './ui/skeleton';
import { ArrowLeftRight, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';
import { fmtUsd, fmtPrice, fmtNum } from '../lib/numfmt';

const FEE = 0.003; // 0.3% swap fee approximation

/**
 * Generic, pool-reserve-driven AMM price-impact simulator. Reserves are supplied
 * by the parent (one fetch shared with the liquidity sim). Constant-product math —
 * exact for v2, a close estimate for v3/v4 concentrated pools.
 */
export default function PoolSwapSimulator({ tokenMeta, pool, reserves, loading, error }) {
  const [side, setSide] = useState('buy');
  const [amount, setAmount] = useState('');

  const sim = useMemo(() => {
    if (!reserves) return null;
    const RtRaw = parseFloat(reserves.token?.reserve);
    const Rb = parseFloat(reserves.base?.reserve);
    const Pb = parseFloat(reserves.base?.usd_price);
    if (!(RtRaw > 0 && Rb > 0)) return { spot: null, lowLiq: true, usdOk: false };
    // Anchor the token reserve to the real market price using the (reliable)
    // base-side depth, so the constant-product curve stays consistent even when
    // the reported token balance is a concentrated-liquidity/DS approximation.
    const marketPrice = reserves.token_price_usd || (Pb ? (Rb * Pb) / RtRaw : null);
    const Rt = (marketPrice > 0 && Rb > 0 && Pb > 0) ? (Rb * Pb) / marketPrice : RtRaw;
    const spot = marketPrice || (Pb ? (Rb * Pb) / Rt : null);
    const supply = parseFloat(tokenMeta?.total_supply);
    const baseUsdReserve = Pb > 0 ? Rb * Pb : null;
    const poolLiq = parseFloat(pool?.liquidity_usd);
    const lowLiq =
      (baseUsdReserve != null && baseUsdReserve < 500) ||
      (isFinite(poolLiq) && poolLiq > 0 && poolLiq < 1000);
    if (lowLiq) return { spot, lowLiq: true, usdOk: Pb > 0, supply };
    const amt = parseFloat(amount);
    const usdOk = Pb > 0;
    let out = { spot, usdOk, supply };
    if (!(amt > 0) || !usdOk) return out;
    if (side === 'buy') {
      const baseIn = amt / Pb;
      const baseInEff = baseIn * (1 - FEE);
      const tokensOut = (Rt * baseInEff) / (Rb + baseInEff);
      const newRb = Rb + baseIn;
      const newRt = Rt - tokensOut;
      const newSpot = (newRb * Pb) / newRt;
      out = {
        ...out,
        received: tokensOut, receivedSym: reserves.token?.symbol,
        avgPrice: amt / tokensOut, newSpot,
        impact: spot ? (newSpot - spot) / spot : null,
        newMc: supply > 0 ? newSpot * supply : null,
      };
    } else {
      const tokenInEff = amt * (1 - FEE);
      const baseOut = (Rb * tokenInEff) / (Rt + tokenInEff);
      const usdOut = baseOut * Pb;
      const newRt = Rt + amt;
      const newRb = Rb - baseOut;
      const newSpot = (newRb * Pb) / newRt;
      out = {
        ...out,
        received: usdOut, receivedSym: 'USD',
        avgPrice: usdOut / amt, newSpot,
        impact: spot ? (newSpot - spot) / spot : null,
        newMc: supply > 0 ? newSpot * supply : null,
      };
    }
    return out;
  }, [reserves, amount, side, tokenMeta, pool]);

  const quicks = side === 'buy' ? [100, 1000, 10000, 100000] : null;

  if (loading) return <Skeleton className="h-72 w-full rounded" data-testid="swap-sim-loading" />;
  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-destructive py-8 justify-center" data-testid="sim-error">
        <AlertCircle className="w-4 h-4" /> {error}
      </div>
    );
  }
  if (!sim) return <div className="text-sm text-muted-foreground py-8 text-center">Select a pool to simulate a swap.</div>;
  if (sim.lowLiq) {
    return (
      <div className="flex flex-col items-center gap-2 text-sm py-10 text-center" data-testid="sim-low-liquidity">
        <AlertCircle className="w-6 h-6 text-warning" />
        <div className="font-semibold">Low-liquidity pool — simulation unavailable</div>
        <p className="text-muted-foreground max-w-md text-xs">
          This pool has too little depth to produce a reliable price-impact estimate. Pick a deeper pool above.
        </p>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="pool-swap-simulator">
      {/* Inputs */}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2" data-testid="sim-side-toggle">
          <Button
            variant={side === 'buy' ? 'default' : 'outline'}
            onClick={() => { setSide('buy'); setAmount(''); }}
            className={side === 'buy' ? 'bg-success text-background hover:opacity-90' : ''}
            data-testid="sim-buy-btn"
          >
            <TrendingUp className="w-4 h-4 mr-1" /> Buy
          </Button>
          <Button
            variant={side === 'sell' ? 'default' : 'outline'}
            onClick={() => { setSide('sell'); setAmount(''); }}
            className={side === 'sell' ? 'bg-destructive text-destructive-foreground hover:opacity-90' : ''}
            data-testid="sim-sell-btn"
          >
            <TrendingDown className="w-4 h-4 mr-1" /> Sell
          </Button>
        </div>

        <div>
          <div className="mono-label mb-1.5">
            {side === 'buy' ? 'Amount to spend (USD)' : `Amount to sell (${tokenMeta?.symbol || 'tokens'})`}
          </div>
          <Input
            type="number"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={side === 'buy' ? '1000' : '1000000'}
            className="font-mono text-lg h-12"
            data-testid="sim-amount-input"
          />
          {quicks && (
            <div className="flex gap-1.5 mt-2">
              {quicks.map((q) => (
                <Button key={q} variant="outline" size="sm" className="text-xs flex-1" onClick={() => setAmount(String(q))} data-testid={`sim-quick-${q}`}>
                  {q >= 1000 ? `$${q / 1000}K` : `$${q}`}
                </Button>
              ))}
            </div>
          )}
        </div>

        {!sim.usdOk && (
          <div className="flex items-center gap-2 text-xs text-warning">
            <AlertCircle className="w-3.5 h-3.5" /> USD pricing unavailable for this pool's base token.
          </div>
        )}

        <div className="text-xs text-muted-foreground border-t border-border pt-3 space-y-1 font-mono">
          <div className="flex justify-between"><span>Pool reserves</span><span>{fmtNum(reserves.token?.reserve)} {reserves.token?.symbol}</span></div>
          <div className="flex justify-between"><span></span><span>{fmtNum(reserves.base?.reserve)} {reserves.base?.symbol}</span></div>
          <div className="flex justify-between"><span>Source</span><span className="uppercase">{reserves.source}</span></div>
        </div>
      </div>

      {/* Results */}
      <div className="space-y-3" data-testid="sim-results">
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-border p-3">
            <div className="mono-label">Current price</div>
            <div className="text-lg font-bold font-mono mt-0.5">{fmtPrice(sim.spot)}</div>
          </div>
          <div className="border border-border p-3">
            <div className="mono-label">{side === 'buy' ? 'Tokens received' : 'USD received'}</div>
            <div className="text-lg font-bold font-mono mt-0.5" data-testid="sim-received">
              {sim.received != null ? (side === 'buy' ? `${fmtNum(sim.received)}` : fmtUsd(sim.received)) : '—'}
            </div>
          </div>
          <div className="border border-border p-3">
            <div className="mono-label">New price</div>
            <div className="text-lg font-bold font-mono mt-0.5" data-testid="sim-new-price">{fmtPrice(sim.newSpot)}</div>
          </div>
          <div className="border border-border p-3">
            <div className="mono-label">Price impact</div>
            <div
              className={`text-lg font-bold font-mono mt-0.5 ${sim.impact >= 0 ? 'text-success' : 'text-destructive'}`}
              data-testid="sim-price-impact"
            >
              {sim.impact != null ? `${sim.impact >= 0 ? '+' : ''}${(sim.impact * 100).toFixed(2)}%` : '—'}
            </div>
          </div>
        </div>
        <div className="border border-border p-3 flex items-center justify-between">
          <div>
            <div className="mono-label">New market cap (est)</div>
            <div className="text-base font-bold font-mono mt-0.5" data-testid="sim-new-mc">{sim.newMc != null ? fmtUsd(sim.newMc) : '—'}</div>
          </div>
          <div className="text-right">
            <div className="mono-label">Avg fill price</div>
            <div className="text-base font-bold font-mono mt-0.5">{sim.avgPrice != null ? fmtPrice(sim.avgPrice) : '—'}</div>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
          <ArrowLeftRight className="w-3 h-3" /> Constant-product estimate incl. {(FEE * 100).toFixed(1)}% fee. v3/v4 concentrated pools may fill better in practice.
        </p>
      </div>
    </div>
  );
}
